import type { ReactNode } from 'react';

/**
 * Lucide line-icons for the wrapper / surface picker — matches the Brew Chat
 * UI design (rounded-square tan badge + monochrome line glyph) instead of the
 * raw emoji the backend ships in `wrapper.icon`.
 *
 * The backend returns an emoji per wrapper (💻 📊 🌱 …); we map that emoji (and,
 * as a fallback, a keyword in the label) to a named lucide glyph. Everything is
 * inline SVG so there's no runtime icon-font dependency.
 */

// Each entry is the inner SVG markup for a 24×24 lucide glyph (stroke-based).
const GLYPHS: Record<string, ReactNode> = {
  'bar-chart-3': (<><path d="M3 3v18h18" /><path d="M18 17V9" /><path d="M13 17V5" /><path d="M8 17v-3" /></>),
  'code-xml': (<><path d="m18 16 4-4-4-4" /><path d="m6 8-4 4 4 4" /><path d="m14.5 4-5 16" /></>),
  globe: (<><circle cx="12" cy="12" r="10" /><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" /><path d="M2 12h20" /></>),
  sprout: (<><path d="M7 20h10" /><path d="M10 20c5.5-2.5.8-6.4 3-10" /><path d="M9.5 9.4c1.1.8 1.8 2.2 2.3 3.7-2 .4-3.5.4-4.8-.3-1.2-.6-2.3-1.9-3-4.2 2.8-.5 4.4 0 5.5.8z" /><path d="M14.1 6a7 7 0 0 0-1.1 4c1.9-.1 3.3-.6 4.3-1.4 1-1 1.6-2.3 1.7-4.6-2.7.1-4 1-4.9 2z" /></>),
  'message-circle': (<><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" /></>),
  eye: (<><path d="M2.06 12.35a1 1 0 0 1 0-.7 10.75 10.75 0 0 1 19.88 0 1 1 0 0 1 0 .7 10.75 10.75 0 0 1-19.88 0" /><circle cx="12" cy="12" r="3" /></>),
  brain: (<><path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" /><path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" /><path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4" /><path d="M6 18a4 4 0 0 1-1.967-.516" /><path d="M19.967 17.484A4 4 0 0 1 18 18" /></>),
  blinds: (<><path d="M3 3h18" /><path d="M20 7H8" /><path d="M20 11H8" /><path d="M10 19h10" /><path d="M8 15h12" /><path d="M4 3v14" /><circle cx="4" cy="19" r="2" /></>),
  'heart-pulse': (<><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" /><path d="M3.22 12H9.5l.5-1 2 4.5 2-7 1.5 3.5h5.27" /></>),
  palette: (<><circle cx="13.5" cy="6.5" r=".5" fill="currentColor" /><circle cx="17.5" cy="10.5" r=".5" fill="currentColor" /><circle cx="8.5" cy="7.5" r=".5" fill="currentColor" /><circle cx="6.5" cy="12.5" r=".5" fill="currentColor" /><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2" /></>),
  landmark: (<><line x1="3" x2="21" y1="22" y2="22" /><line x1="6" x2="6" y1="18" y2="11" /><line x1="10" x2="10" y1="18" y2="11" /><line x1="14" x2="14" y1="18" y2="11" /><line x1="18" x2="18" y1="18" y2="11" /><polygon points="12 2 20 7 4 7" /></>),
  scale: (<><path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" /><path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" /><path d="M7 21h10" /><path d="M12 3v18" /><path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2" /></>),
  flame: (<><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" /></>),
  gem: (<><path d="M6 3h12l4 6-10 13L2 9Z" /><path d="M11 3 8 9l4 13 4-13-3-6" /><path d="M2 9h20" /></>),
  vote: (<><path d="m9 12 2 2 4-4" /><path d="M5 7c0-1.1.9-2 2-2h10a2 2 0 0 1 2 2v12H5V7Z" /><path d="M22 19H2" /></>),
  coffee: (<><path d="M10 2v2" /><path d="M14 2v2" /><path d="M16 8a1 1 0 0 1 1 1v8a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1h14a4 4 0 1 1 0 8h-1" /><path d="M6 2v2" /></>),
  sparkles: (<><path d="M9.94 15.5A2 2 0 0 0 8.5 14.06l-6.14-1.58a.5.5 0 0 1 0-.96L8.5 9.94A2 2 0 0 0 9.94 8.5l1.58-6.14a.5.5 0 0 1 .96 0L14.06 8.5A2 2 0 0 0 15.5 9.94l6.14 1.58a.5.5 0 0 1 0 .96L15.5 14.06a2 2 0 0 0-1.44 1.44l-1.58 6.14a.5.5 0 0 1-.96 0z" /><path d="M20 3v4" /><path d="M22 5h-4" /><path d="M4 17v2" /><path d="M5 18H3" /></>),
  terminal: (<><path d="m4 17 6-6-6-6" /><path d="M12 19h8" /></>),
  mail: (<><rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></>),
  'file-text': (<><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="M10 9H8" /><path d="M16 13H8" /><path d="M16 17H8" /></>),
  presentation: (<><path d="M2 3h20" /><path d="M21 3v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3" /><path d="m7 21 5-5 5 5" /></>),
  database: (<><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5V19A9 3 0 0 0 21 19V5" /><path d="M3 12A9 3 0 0 0 21 12" /></>),
  lightbulb: (<><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" /><path d="M9 18h6" /><path d="M10 22h4" /></>),
  'search-code': (<><path d="m9 9-2 2 2 2" /><path d="m13 13 2-2-2-2" /><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></>),
};

// Map the backend emoji → a named glyph. The default ('🔥') and unknown
// wrappers fall back via keyword matching, then to 'sparkles'.
const EMOJI_TO_GLYPH: Record<string, string> = {
  '💻': 'code-xml',
  '⭐': 'globe',
  '🌱': 'sprout',
  '📊': 'bar-chart-3',
  '💬': 'message-circle',
  '🏦': 'landmark',
  '⚖️': 'scale',
  '🩺': 'heart-pulse',
  '🧠': 'brain',
  '👁️': 'eye',
  '🪟': 'blinds',
  '🎨': 'palette',
  '🔥': 'flame',
  '💎': 'gem',
  '🗳️': 'vote',
  '☕': 'coffee',
  '✉️': 'mail',
  '📧': 'mail',
  '📨': 'mail',
  '📝': 'file-text',
  '📄': 'file-text',
  '🎬': 'presentation',
  '📽️': 'presentation',
  '🖥️': 'terminal',
  '⌨️': 'terminal',
};

// Keyword fallback when the emoji isn't recognised — keys off the display label.
// More-specific rules first so e.g. "Marp CLI" reads as a deck, not a terminal.
const KEYWORD_TO_GLYPH: Array<[RegExp, string]> = [
  [/marp|pptx|presentation|deck|slide/i, 'presentation'],
  [/\bcli\b|terminal|shell|command/i, 'terminal'],
  [/pandoc|markdown|document|\bdocx?\b|\bpdf\b/i, 'file-text'],
  [/outreach|email|\bmail\b/i, 'mail'],
  [/database|\bsql\b|query|warehouse/i, 'database'],
  [/review/i, 'search-code'],
  [/explain|eli5|simpl|idea/i, 'lightbulb'],
  [/code|engineer|develop/i, 'code-xml'],
  [/data|bi|sql|chart|dashboard|analyt/i, 'bar-chart-3'],
  [/esg|sustain|brsr|gri/i, 'sprout'],
  [/vision|image|ocr/i, 'eye'],
  [/reason|proof|think/i, 'brain'],
  [/health|clinic|medical/i, 'heart-pulse'],
  [/lumara|window|blind|cover/i, 'blinds'],
  [/design|brand|deck|brochure/i, 'palette'],
  [/bfsi|bank|financ|insur/i, 'landmark'],
  [/legal|law|contract|judg/i, 'scale'],
  [/pro|premium|search|web/i, 'globe'],
  [/elect|vote|booth/i, 'vote'],
  [/native|chat|brew/i, 'message-circle'],
];

/** Resolve a backend icon (emoji) + label to a named lucide glyph. */
export function glyphNameFor(icon: string | null | undefined, label = ''): string {
  if (icon) {
    if (GLYPHS[icon]) return icon; // already a glyph name
    const byEmoji = EMOJI_TO_GLYPH[icon] ?? EMOJI_TO_GLYPH[icon.replace(/️/g, '')];
    if (byEmoji) return byEmoji;
  }
  for (const [re, name] of KEYWORD_TO_GLYPH) if (re.test(label)) return name;
  return 'sparkles';
}

/** Bare line-glyph SVG (no badge) — used inline, e.g. on the picker trigger. */
export function PickerGlyphSvg({
  icon, label, size = 18, strokeWidth = 1.7,
}: { icon?: string | null; label?: string; size?: number; strokeWidth?: number }) {
  const name = glyphNameFor(icon, label);
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={strokeWidth}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    >
      {GLYPHS[name]}
    </svg>
  );
}

/** Rounded-square tan badge wrapping a line-glyph — the picker row "logo". */
export function PickerGlyph({
  icon, label, size = 30,
}: { icon?: string | null; label?: string; size?: number }) {
  return (
    <span className="picker-glyph" style={{ width: size, height: size }}>
      <PickerGlyphSvg icon={icon ?? null} label={label ?? ''} size={Math.round(size * 0.55)} />
    </span>
  );
}
